import { z } from 'zod';
import { bopApi } from '@/lib/api/client';
import { getFundsCustomer } from '@/services/api/user-access';
import type { UserSegment } from '@/services/api/user-console';
import { formatDateLocale } from '@/utils/format';

const ANSWERS_PATH = 'v1/questionnaire/answers';
const DEFAULT_AML_THRESHOLDS_PATH = 'v1/system/aml-thresholds';
const AML_THRESHOLD_UPDATE_PATH = 'v1/questionnaire/aml-threshold/update';
const AML_RISK_LEVEL_UPDATE_PATH = 'v1/questionnaire/aml-risk-level/update';
const INSTITUTION_DETAILS_UPDATE_PATH = 'v1/questionnaire/institution-details/update';
const INSTITUTION_DETAILS_META_PATH = 'v1/questionnaire/institution-details/meta';

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

export interface QuestionnaireMeta {
  /** Marks an AML threshold as inherited from the segment default */
  is_default?: boolean;
  /** Futures appropriateness: the attempt's stored date exactly as saved, which the explain endpoint matches on */
  date?: string;
}

const FUTURES_APPROPRIATENESS_NAME = 'Futures Appropriateness';

/** bop-api names a user's futures appropriateness attempts 'Futures Appropriateness' or 'Futures Appropriateness (Attempt n)' */
export const isFuturesAppropriateness = (questionnaire: { s: string }): boolean =>
  questionnaire.s === FUTURES_APPROPRIATENESS_NAME || questionnaire.s.startsWith(`${FUTURES_APPROPRIATENESS_NAME} (`);

/**
 * Raw wire row from POST /v1/questionnaire/answers. `s` is already the friendly
 * display name after bop-api's rename; `terms-*` rows pass through
 * unrenamed.
 */
export interface RawQuestionnaire {
  /** PID (partition key). Optional only because the synthetic default-AML row omits it (legacy parity — no PID row renders on that tab). */
  p?: string;
  /** Questionnaire name, e.g. 'AML', 'Institution Details', 'terms-2025-04-06' */
  s: string;
  /** Answers — object or JSON string depending on questionnaire */
  a?: unknown;
  /** Completion date — ISO string or unix epoch (seconds OR millis) */
  d?: string | number;
  /** AML: risk string 's=<score>,l=<level>' | Institution Details: regulated | Verification/default: result */
  r?: unknown;
  /** AML: threshold (EUR) */
  t?: number;
  /** Institution Details: client categorisation | NCI: country */
  c?: unknown;
  /** NCI: identifier value */
  n?: string;
  /** NCI: 'CCPT' | 'NIDN' | 'CONCAT' */
  id_type?: string;
  id_sub_type?: string;
  /** Verification: level name */
  l?: string;
  /** Verification: review answer */
  ra?: string;
  /** Verification: review status */
  rs?: string;
  /** Institution Details: the version its answers were collected against; absent on records from before versioning */
  version?: number;
  _meta?: QuestionnaireMeta;
  /** Any other Dynamo attribute passes through untouched */
  [key: string]: unknown;
}

/** Display shape after the terse-key → label transform (legacy types/questionnaire.ts — deliberately loose) */
export type Questionnaire = { s: string; _meta?: QuestionnaireMeta } & Record<string, unknown>;

export interface DefaultAmlThresholds {
  RETAIL: number;
  INSTITUTIONAL: number;
  MARKET_MAKER: number;
  /** Absent until bop-api's system record is seeded with the risk-based mapping */
  byRiskLevel?: Record<'LOW' | 'MEDIUM' | 'HIGH', number>;
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------

// types/aml.ts
export const AML_RISK_LEVELS = { LOW: 1, MEDIUM: 2, HIGH: 3, PROHIBITED: 4 } as const;
export type AmlRiskLevel = (typeof AML_RISK_LEVELS)[keyof typeof AML_RISK_LEVELS];
export const AML_RISK_LEVEL_VALUES = [AML_RISK_LEVELS.LOW, AML_RISK_LEVELS.MEDIUM, AML_RISK_LEVELS.HIGH, AML_RISK_LEVELS.PROHIBITED] as const;
/** v2 labels the numeric levels (legacy showed bare numbers) */
export const AML_RISK_LEVEL_LABELS: Record<AmlRiskLevel, string> = {
  [AML_RISK_LEVELS.LOW]: '1 — LOW',
  [AML_RISK_LEVELS.MEDIUM]: '2 — MEDIUM',
  [AML_RISK_LEVELS.HIGH]: '3 — HIGH',
  [AML_RISK_LEVELS.PROHIBITED]: '4 — PROHIBITED',
};

// ---------------------------------------------------------------------------
// Terse-key → label mapping (moved from the Nuxt BFF)
// ---------------------------------------------------------------------------

interface QuestionnaireConfig {
  fieldMappings: Record<string, string>;
  requiredFields?: readonly string[];
}

const COMMON_FIELD_MAPPINGS = { a: 'Answers', s: 'Questionnaire', p: 'PID', d: 'Date' } as const;

const DEFAULT_QUESTIONNAIRE_CONFIG: QuestionnaireConfig = { fieldMappings: { ...COMMON_FIELD_MAPPINGS, r: 'Result' } };

const QUESTIONNAIRE_CONFIG: Record<string, QuestionnaireConfig> = {
  AML: {
    fieldMappings: { ...COMMON_FIELD_MAPPINGS, r: 'AML Risk', t: 'AML Threshold' },
    // Forces the 'AML Risk'/'AML Threshold' rows to render even when absent (value null)
    requiredFields: ['AML Risk', 'AML Threshold'],
  },
  'Institution Details': { fieldMappings: { ...COMMON_FIELD_MAPPINGS, r: 'Regulated', c: 'Client Categorisation' } },
  'National Client Identifier': {
    fieldMappings: {
      ...COMMON_FIELD_MAPPINGS,
      c: 'Country',
      n: 'National Client Identifier',
      id_type: 'National Client Identifier Type',
      id_sub_type: 'National Client Identifier Sub Type',
    },
  },
  Verification: { fieldMappings: { ...COMMON_FIELD_MAPPINGS, l: 'Level Name', r: 'Result', ra: 'Review Answer', rs: 'Review Status' } },
};

/**
 * Terse Dynamo keys → display labels; mapping order defines row order, every
 * unmapped raw key passes through under its raw name. Returns a fresh
 * object — the raw row is never mutated.
 */
export const transformQuestionnaire = (raw: RawQuestionnaire): Questionnaire => {
  const config = QUESTIONNAIRE_CONFIG[raw.s] ?? DEFAULT_QUESTIONNAIRE_CONFIG;
  const out: Record<string, unknown> = { s: raw.s }; // keep the machine key (hidden by the display layer)
  for (const [key, label] of Object.entries(config.fieldMappings)) {
    if (raw[key] !== undefined || config.requiredFields?.includes(label)) {
      // Completion dates render in the operator locale (utils/format.formatDateLocale
      // is the shared port of the same legacy helpers/formatDate.js this file carried)
      out[label] = key === 'd' && raw[key] ? formatDateLocale(raw[key] as string | number) : (raw[key] ?? null);
    }
  }
  for (const [key, value] of Object.entries(raw)) {
    if (!(key in config.fieldMappings)) out[key] = value ?? null;
  }
  if (isFuturesAppropriateness(raw) && typeof raw.d === 'string') {
    out._meta = { ...(out._meta as QuestionnaireMeta | null | undefined), date: raw.d };
  }
  return out as Questionnaire;
};

// ---------------------------------------------------------------------------
// AML risk string parsing
// ---------------------------------------------------------------------------

/** `r` on the AML row is a Dynamo-encoded string 's=<score>,l=<level>' (e.g. 's=12,l=2') */
const AML_RISK_PATTERN = /s=(\d+),l=(\d+)/;

export const parseAmlRisk = (r: unknown): { score: number; level: number } | null => {
  if (typeof r !== 'string') return null;
  const match = AML_RISK_PATTERN.exec(r);
  if (!match) return null;
  return { score: Number.parseInt(match[1], 10), level: Number.parseInt(match[2], 10) };
};

// ---------------------------------------------------------------------------
// GET pipeline: strip → inject → transform
// ---------------------------------------------------------------------------

/**
 * ⚠⚠⚠ SECURITY-CRITICAL — DO NOT REMOVE OR BYPASS ⚠⚠⚠
 *
 * bop-api's getUserAnswers does NOT exclude the `personal-identity` row: it comes
 * back renamed to 'Personal Identity' and contains raw PII. PII access is
 * justification-gated on the PII tab; leaking it through this endpoint would
 * bypass the audit trail entirely. The legacy Nuxt BFF stripped it server-side;
 * v2 calls bop-api directly, so this client-side strip runs INSIDE the service —
 * before anything is cached, rendered, or logged — so no call site can forget it.
 *
 * Ideal fix is server-side (add `personal-identity` to EXCLUDE_KEYS in
 * bop-api/serverless/src/lambdas/questionnaire/getUserAnswers.js — tracked
 * separately), but this filter ships regardless as defence in depth.
 */
export const stripPii = (items: RawQuestionnaire[]): RawQuestionnaire[] => items.filter((item) => item.s !== 'Personal Identity');

const RISK_BASED_LEVEL_KEYS: Partial<Record<number, 'LOW' | 'MEDIUM' | 'HIGH'>> = {
  [AML_RISK_LEVELS.LOW]: 'LOW',
  [AML_RISK_LEVELS.MEDIUM]: 'MEDIUM',
  [AML_RISK_LEVELS.HIGH]: 'HIGH',
};

/**
 * Mirrors bop-api's resolveDefaultAmlThreshold so the displayed default matches what the
 * compliance checks enforce: users with an assessed risk level get the risk-based value;
 * everyone else keeps the segment default (PROHIBITED, unassessed users, and pre-seed
 * records without byRiskLevel).
 */
export const resolveDefaultAmlThreshold = (thresholds: DefaultAmlThresholds, segment: UserSegment, riskLevel: number | undefined): number | null => {
  const levelKey = riskLevel === undefined ? undefined : RISK_BASED_LEVEL_KEYS[riskLevel];
  const riskBasedThreshold = levelKey ? thresholds.byRiskLevel?.[levelKey] : undefined;
  if (typeof riskBasedThreshold === 'number') return riskBasedThreshold;
  return thresholds[segment] ?? null;
};

const fetchDefaultAmlThresholdForUser = async (pid: string, riskLevel: number | undefined): Promise<number | null> => {
  try {
    const [fundsCustomer, thresholds] = await Promise.all([getFundsCustomer(pid), bopApi.get(DEFAULT_AML_THRESHOLDS_PATH).json<DefaultAmlThresholds>()]);
    const segment = fundsCustomer.segment;
    if (!segment) return null;
    return resolveDefaultAmlThreshold(thresholds, segment, riskLevel);
  } catch {
    // A funds 404 means the user is unknown to the funds service — the
    // shared getFundsCustomer synthesizes a segment-less default for that case, so
    // it lands on the `!segment` branch above. ANY other failure in these two
    // lookups only skips the default-threshold injection and must never fail the
    // questionnaires load.
    return null;
  }
};

/**
 * Default-AML-threshold injection. Runs on the RAW rows, after the PII
 * strip and before the label transform. Legacy rationale:
 *
 * Some users don't have an AML questionnaire, but we still display it so we can:
 * - View their default AML threshold based on their segment
 * - Set a custom AML threshold
 * - Set a custom AML risk level
 *
 * Similarly, some users have an AML questionnaire but don't have a custom
 * threshold, so we populate it with the default threshold based on their segment.
 * Either way `_meta.is_default: true` marks the value as inherited.
 */
export const injectDefaultAmlThreshold = async (pid: string, rows: RawQuestionnaire[]): Promise<RawQuestionnaire[]> => {
  const amlRow = rows.find((row) => row.s === 'AML');
  if (amlRow && 't' in amlRow) return rows;
  const riskLevel = amlRow ? parseAmlRisk(amlRow.r)?.level : undefined;
  const defaultThreshold = await fetchDefaultAmlThresholdForUser(pid, riskLevel);
  if (defaultThreshold === null) return rows;
  // The synthetic row is unshifted (AML appears first); a patched existing row keeps its sorted position
  if (!amlRow) return [{ s: 'AML', t: defaultThreshold, _meta: { is_default: true } }, ...rows];
  // Immutably in v2 (legacy mutated the fetched row in place)
  return rows.map((row) => (row === amlRow ? { ...row, t: defaultThreshold, _meta: { is_default: true } } : row));
};

/**
 * Full questionnaires pipeline: fetch → PII strip → default-threshold injection →
 * terse-key transform. The result is SHARED with the Terms tab (`terms-*` rows
 * ride along untransformed-by-name — the Terms tab filters them out of this same
 * cached array).
 */
export const getQuestionnaireAnswers = async (pid: string): Promise<Questionnaire[]> => {
  const rawRows = await bopApi.post(ANSWERS_PATH, { json: { pid } }).json<RawQuestionnaire[]>();
  const safeRows = stripPii(rawRows);
  const rowsWithDefault = await injectDefaultAmlThreshold(pid, safeRows);
  return rowsWithDefault.map(transformQuestionnaire);
};

// ---------------------------------------------------------------------------
// Mutations — all three return 200 with an EMPTY body; never
// .json() them.
// ---------------------------------------------------------------------------

/** Server threshold range, surfaced client-side so the operator sees a field error */
export const AML_THRESHOLD_MIN = 1;
export const AML_THRESHOLD_MAX = 10_000_000;

export interface UpdateAmlThresholdRequest {
  pid: string;
  /** Integer/float EUR; server range 1 … 10_000_000 */
  threshold: number;
  /** Audit justification (min 1 server-side; UI enforces 35–500) */
  comment: string;
}

// Legacy Nuxt-route validation kept client-side: pid uuid, comment non-empty;
// the threshold range mirrors the server.
const updateAmlThresholdSchema = z.object({
  pid: z.uuid(),
  threshold: z.number().min(AML_THRESHOLD_MIN).max(AML_THRESHOLD_MAX),
  comment: z.string().min(1),
});

export const updateAmlThreshold = (body: UpdateAmlThresholdRequest): Promise<string> =>
  bopApi.post(AML_THRESHOLD_UPDATE_PATH, { json: updateAmlThresholdSchema.parse(body) }).text();

export interface UpdateAmlRiskLevelRequest {
  pid: string;
  /** LOW=1, MEDIUM=2, HIGH=3, PROHIBITED=4 */
  riskLevel: AmlRiskLevel;
  comment: string;
}

const updateAmlRiskLevelSchema = z.object({
  pid: z.uuid(),
  riskLevel: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  comment: z.string().min(1),
});

export const updateAmlRiskLevel = (body: UpdateAmlRiskLevelRequest): Promise<string> =>
  bopApi.post(AML_RISK_LEVEL_UPDATE_PATH, { json: updateAmlRiskLevelSchema.parse(body) }).text();

export interface UpdateInstitutionDetailsInput {
  pid: string;
  comment: string;
  /** One FULL updated section, keyed by its name */
  answers: Record<string, unknown>;
  /** The record's version, sent back unchanged: fast-api rejects one that differs from the record's and reads a missing one as V1 */
  version?: number;
  /** Saves to the draft upgrading the record instead, which replaces it once complete */
  draft?: boolean;
}

const updateInstitutionDetailsSchema = z.object({ pid: z.uuid(), comment: z.string().min(1) });

/** The answers go on the wire wrapped in `data`; wrapping them here means no call site can send them unwrapped */
export const updateInstitutionDetails = ({ pid, comment, answers, version, draft }: UpdateInstitutionDetailsInput): Promise<string> => {
  updateInstitutionDetailsSchema.parse({ pid, comment });
  // One section per save, so each audit entry is about a single section
  if (Object.keys(answers).length !== 1) {
    throw new Error('Institution-details answers must hold exactly one section');
  }
  return bopApi.post(INSTITUTION_DETAILS_UPDATE_PATH, { json: { pid, comment, data: { version, answers }, draft } }).text();
};

export interface InstitutionDetailsCapabilities {
  canEdit: boolean;
}

export const getInstitutionDetailsMeta = () => bopApi.get(INSTITUTION_DETAILS_META_PATH).json<{ capabilities: InstitutionDetailsCapabilities }>();
